<script>
export default {
	name: 'SideSheetOverlay',
	inheritAttrs: false,
};
</script>

<script setup>
import { ref, computed, nextTick } from 'vue';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import ExperienceFragment from '~/aem-components/ExperienceFragment.vue';
import { useSideSheetOverlay } from '~/logic/composables/useSideSheetOverlay';
import { useEventBus } from '@vueuse/core';
import { isMobileViewport } from '~/logic/composables/breakpoints.js';
import Anchor from '~/logic/helpers/Anchor';

const rootEl = ref(null);
const siteName = window.siteName;
const { hasFocus, activate: activateFocusTrap, deactivate: deactivateFocusTrap } = useFocusTrap(rootEl);

const onRootMounted = () => Anchor.activate('body');
const handleRootElOnMounted = async () => {
	rootEl.value?.focus();
	await nextTick();
	activateFocusTrap();
};

const handleRootElBeforeUnmount = () => deactivateFocusTrap();

const { overlayData, removeOverlayID, isVisible } = useSideSheetOverlay();
const closeOverlay = () => {
	if (overlayData.value?.displayType === 'notification') {
		if (isNotificationOpened.value) {
			enrichNotificationClosed.emit({ active: false });
			isNotificationOpened.value = false;
			return;
		}
		showMenu.value = false;
	}
	removeOverlayID();
};

const overlayStyles = computed(() => {
	if (isMobileViewport.value) return {};

	const widths = {
		'flightSearch-hotels': '768px',
		'mid-tier-status-benefits': '640px',
		'notification': '640px',
		'half-page-dimension': '50%',
	};
	
	const displayType = overlayData.value?.displayType;
	return {
		width: widths[displayType] || '100%',
	};
});
const isFlightSearchMobile = computed(() => isMobileViewport.value && overlayData.value?.displayType === 'flightSearch-hotels'
);

const isGiftCardQuickRedemption = computed(() => overlayData.value?.displayType === 'quick-redemption');

const mobileOverlayContainerClass = computed(() => {
	return isMobileViewport.value && overlayData.value?.displayType === 'notification'
		? 'overlay-container'
		: 'overlay-bottom-container';
});

const showCloseButton = computed(() => !(isMobileViewport.value && overlayData.value?.displayType !== 'notification'));
const transitionName = computed(() => {
	if (isMobileViewport.value && overlayData.value?.displayType !== 'notification') return 'overlay-bottom';

	return 'overlay-drawer-right';
});
const showMenu = ref(false);
const toggleMenu = () => {
	showMenu.value = !showMenu.value;
};

const isNotificationOpened = ref(false);
const enrichRead = ref('');
const enrichReadAll = ref('');
const enrichDelete = ref('');
const enrichDeleteAll = ref('');
const enrichNotificationOpened = useEventBus('enrich-notification-opened');
const enrichNotificationClosed = useEventBus('enrich-notification-closed');
const enrichNotificationLabels = useEventBus('enrich-notification-labels');

enrichNotificationOpened.on((event) => {
	if (event.active) {
		isNotificationOpened.value = true;
		showMenu.value = false;
	}

});

// labels
enrichNotificationLabels.on((event) => {
	if (overlayData.value?.displayType === 'notification') {
		if (event.readLabel) enrichRead.value = event.readLabel;
		if (event.readAllLabel) enrichReadAll.value = event.readAllLabel;
		if (event.deleteLabel) enrichDelete.value = event.deleteLabel;
		if (event.deleteAllLabel) enrichDeleteAll.value = event.deleteAllLabel;
	}
});
const enrichNotificationBus = useEventBus('enrich-notification');
const handleMarkAllAsRead = () => {
	enrichNotificationBus.emit({ action: 'markAllAsRead', status: 2 });
	showMenu.value = false;
};
const handleDeleteAction = () => {
	if (isNotificationOpened.value) {
		enrichNotificationBus.emit({ action: 'deleteOne', status: 0 });
	} else {
		enrichNotificationBus.emit({ action: 'deleteAll', status: 0 });
	}
	showMenu.value = false;
	isNotificationOpened.value = false;
};

</script>

<template>
<!-- Non-mobile view overlay -->
<Teleport v-if="!isFlightSearchMobile && !isGiftCardQuickRedemption" to="body">
	<Transition ref="rootEl" :name="transitionName" appear>
		<div
			v-if="isVisible"
			ref="rootEl"
			class="SideSheetOverlay"
			:class="isMobileViewport ? 'backdrop-bottom' : 'backdrop'"
			:data-use-theme="siteName"
			tabindex="-1"
			@vue:mounted="handleRootElOnMounted"
			@vue:beforeUnmount="handleRootElBeforeUnmount"
		>
			<div
				class="h-full"
				:class="isMobileViewport ? mobileOverlayContainerClass : 'overlay-container'"
				:style="overlayStyles"
			>
				<div class="overlay-wrapper">
					<div class="overlay-line" @click="removeOverlayID"></div>
					<div
						class="drawer-header mb-3"
						:class="{ 'border-bottom': (isMobileViewport && !showCloseButton) }"
					>
						<div class="text-sm overlay-title heading-5">{{ overlayData.overlayTitle }}</div>
						<template v-if="overlayData.displayType === 'notification'">
							<button
								type="button"
								class="w-8 h-8 p-0 absolute right-14  flex items-center justify-center hover:bg-neutral-grey-extralight rounded-full"
								@click="toggleMenu"
							>
								<icon-fas-ellipsis-vertical class="w-3 h-3" />
							</button>
							<div
								v-if="showMenu"
								class="absolute right-12 top-12 bg-white border rounded shadow-lg z-50 w-44"
							>
								<ul>
									<li>
										<button
											v-if="!isNotificationOpened"
											type="button"
											class="block w-full text-left px-4 py-2 hover:bg-neutral-grey-extralight"
											@click="handleMarkAllAsRead"
										>
											{{ enrichReadAll }}
										</button>
									</li>
									<li>
										<button
											type="button"
											class="block w-full text-left px-4 py-2 hover:bg-neutral-grey-extralight"
											@click="handleDeleteAction"
										>
											{{ isNotificationOpened ? enrichDelete : enrichDeleteAll }}
										</button>
									</li>
								</ul>
							</div>
						</template>
						<button
							v-if="showCloseButton"
							type="button"
							class="w-8 h-8 my-6 p-0 md:h-6 md:w-6 md:my-4 flex items-center justify-center"
							@click="closeOverlay"
						>
							<icon-far-xmark class="w-2.5" />
						</button>
					</div>
					<div
						class="overlay-content p-4 flex-grow"
						:class="{ 'overflow-y-auto styled-scrollbar': overlayData.displayType !== 'notification' }"
					>
						<ExperienceFragment :xfPath="overlayData?.experienceFragmentURL" />
					</div>
				</div>
			</div>
		</div>
	</Transition>
</Teleport>

<!-- Mobile view for specific flight search -->
<Teleport v-else-if="!isGiftCardQuickRedemption" to="#booking-subpage-container">
	<section class="section-banner" :data-use-theme="siteName" @vue:mounted="onRootMounted">
		<div class="generic-container">
			<button class="btn-back flex items-center text-white gap-2" type="button" @click="removeOverlayID">
				<icon-far-chevron-left class="fill-current transform rtl:-rotate-180" />
				<span class="">{{ $t('Back') }}</span>
			</button>
			<div class="my-10 text-white">
				<h1 class="text-center">{{ overlayData.overlayTitle }}</h1>
			</div>
		</div>
	</section>
	<div class="top-rounded-section-breaker"></div>
	<div class="mobile-flightsearch-overlay p-3" :data-use-theme="siteName" @vue:mounted="onRootMounted">
		<ExperienceFragment :xfPath="overlayData?.experienceFragmentURL" />
	</div>
</Teleport>

<Teleport v-else to="body">
	<Transition ref="rootEl" name="overlay-bottom" appear>
		<div
			v-if="isVisible"
			:class="isMobileViewport ? 'backdrop-bottom' : 'backdrop-center'"
			:data-use-theme="siteName"
			tabindex="-1"
			@vue:mounted="handleRootElOnMounted"
			@vue:beforeUnmount="handleRootElBeforeUnmount"
		>
			<div
				class="quick-redemption-overlay-container rounded-xl"
				:class="{
					'quick-redemption-overlay-mobile-container': isMobileViewport,
				}"
			>
				<div class="overlay-wrapper">
					<!-- <div class="overlay-line" @click="removeOverlayID"></div>
				<div
					class="drawer-header mb-3"
					:class="{ 'border-bottom': (isMobileViewport && !showCloseButton) }"
				>
					<div class="text-sm overlay-title heading-5">{{ overlayData.overlayTitle }}</div>
					<button
						v-if="showCloseButton"
						type="button"
						class="w-8 h-8 my-6 p-0 md:h-6 md:w-6 md:my-4 flex items-center justify-center"
						@click="removeOverlayID"
					>
						<icon-far-xmark class="w-2.5" />
					</button>
				</div> -->
					<div
						class="overlay-content flex-grow overflow-y-auto styled-scrollbar"
					>
						<ExperienceFragment :xfPath="overlayData?.experienceFragmentURL" />
					</div>
				</div>
			</div>
		</div>
	</Transition>
</Teleport>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.SideSheetOverlay {
	:deep(.toggle-group) {
		@apply ml-0 pt-15;
	}

	:deep(.field-wrapper) {
		flex-direction: column;
	}

	:deep(.component-wrapper) {
		width: 100% !important;
	}

	:deep(.cta-wrapper){
		@apply m-auto;
	}

	:deep(.DateRangePicker .floating-panel) {
		width: 100%;
	}

	:deep(.picker-wrapper-to .multiselect-dropdown) {
		left: 8px;
	}

	:deep(.SubTabTermsConditions .tnc-content) {
		max-height: calc(100dvh - 220px);
		
		@media #{var.$query-max-md} {
			max-height: 100%;
		};
	}
}

.backdrop,
.backdrop-bottom,
.backdrop-center {
	position: fixed;
	z-index: 10000;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: color.change(black, $alpha: 0.8);
	display: flex;
	justify-content: flex-end;
	transition: opacity 0.45s ease;
}

.backdrop-bottom {
	align-items: flex-end;
	justify-content: center;
	transition: opacity 0.3s ease;
}

.backdrop-center {
	align-items: center;
	justify-content: center;
	transition: opacity 0.3s ease;
}

.overlay-bottom-container,
.overlay-container {
	background-color: white;
	width: 100%;
	overflow: hidden;
	@apply pb-4;
}

.quick-redemption-overlay-container {
	background-color: white;
	width: 900px;
	height: auto;
}

.quick-redemption-overlay-mobile-container {
	background-color: white;
	width: 100%;
	overflow: hidden;
	height: auto;
}

.drawer-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	@apply px-6;
	border-bottom-width: 1px;
	border-bottom-color: var(--neutral-grey-base);

	&.border-bottom {
		border-bottom: none;
	}
}

.overlay-bottom-container {
	border-top-left-radius: 1rem;
	border-top-right-radius: 1rem;

	.overlay-line {
		@apply pb-7 pt-4;

		&::after {
			content: '';
			width: 48px;
			height: 2px;
			display: block;
			margin: 0 auto;
			background-color: var(--neutral-grey-base);
			@apply rounded;
		}
	}
}

.overlay-wrapper {
	height: 100%;
	max-height: inherit;
	display: flex;
	flex-direction: column;
}

/* Animations */
.overlay-bottom-enter-from {
	opacity: 0;
}

.overlay-bottom-enter-active,
.overlay-bottom-leave-active {
	transition: opacity 200ms;
}

.overlay-bottom-leave-to {
	opacity: 0;
}

.overlay-bottom-enter-from,
.overlay-bottom-leave-to {
	.overlay-container,
	.quick-redemption-overlay-mobile-container {
		transform: translateY(75%);
	}
}

.overlay-bottom-enter-active,
.overlay-bottom-leave-active {
	.overlay-container,
	.quick-redemption-overlay-mobile-container {
		transition: transform 200ms;
	}
}

.overlay-bottom-enter-to,
.overlay-bottom-leave-from {
	.overlay-container,
	.quick-redemption-overlay-mobile-container {
		transform: translateY(0%);
	}
}

.overlay-drawer-right-enter-from {
	opacity: 0;
}

.overlay-drawer-right-enter-active,
.overlay-drawer-right-leave-active {
	transition: opacity 200ms;
}

.overlay-drawer-right-leave-to {
	opacity: 0;
}

.overlay-drawer-right-enter-from,
.overlay-drawer-right-leave-to {
	.overlay-container {
		transform: translateX(75%);
	}
}

.overlay-drawer-right-enter-active,
.overlay-drawer-right-leave-active {
	.overlay-container {
		transition: transform 200ms;
	}
}

.section-banner {
	--batik-bg-colour: var(--primary-blue-base);
	--batik-pattern-img: url('~/assets/batik-pattern.svg');
	background-color: var(--batik-bg-colour);
	background-image: var(--batik-pattern-img);
	background-size: 200px auto;
	background-repeat: repeat;
	height: 317px;
	overflow: hidden;
}

.btn-back {
	margin-top: var(--headerHeight);
}

.top-rounded-section-breaker {
	border-top-left-radius: 24px;
	border-top-right-radius: 24px;
	--bgColor: white;
	background-color: var(--bgColor);
	height: 45px;
	margin-top: -44px;
}

.section-banner[data-use-theme="MHH"] {
	--batik-bg-colour: var(--primary-mhh-teal-base);
	--batik-pattern-img: url('~/assets/floral-pattern-short-banner.svg');
	background-size: cover;
	background-position-y: 45px;
	background-repeat: no-repeat;
}
</style>